import { jwtDecode } from "jwt-decode";
import { createContext, useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ClientsDataContextProvider } from "../pages/clients/ClientDataContext";
import { ClientsLeadsDataContextProvider } from "../pages/clientsleads/ClientsLeadsContext";
import { VendorsDataContextProvider } from "../pages/vendors/VendorDataContext";

import { ClientsLeadsNuevoDataContextProvider } from "../pages/clientsleadsnuevo/ClientsLeadsNuevoContext";


import { CompaniesDataContextProvider } from "../pages/companies/CompaniesDataContext";
import { CustomerStatusDataContextProvider } from "../pages/customerStatus/CustomerStatusDataContext";
import { LeadTagDataContextProvider } from "../pages/leadTag/LeadTagDataContext";
import { CountryPhoneContextProvider } from "../pages/countryPhone/CountryPhoneContext";
import { SidebarDataContextProvider } from "../pages/global/SidebarDataContext";
import { WebSocketProvider } from "./WebSocketContext";
import { setAuthData } from "../utils/LocalStorageManager";
import { TemplatesQuickResponseDataContextProvider } from "../pages/templatesQuickResponse/TemplatesQuickResponseDataContext";
import { TemplatesTwilioDataContextProvider } from "../pages/templatesTwilio/TemplatesTwilioDataContext";
import { TemplatesEmailDataContextProvider } from "../pages/templatesEmail/TemplatesEmailDataContext";
import { AlertProvider } from "./AlertContext";

const initialState = {
  isLoading: false,
  errorMsg: "",
  successMsg: "",
};

const AppContext = createContext(initialState);
const AppProvider = ({ children, authData, isEmbedded = false }) => {
  const [authInfo, setAuthInfo] = useState(authData);
  const [loading, setLoading] = useState(initialState.isLoading);
  const [errorMsg, setErrorMsg] = useState(initialState.errorMsg);
  const [successMsg, setSuccessMsg] = useState(initialState.successMsg);
  const [modalData, setModalData] = useState({
    modalOpen: false,
    modalType: "",
    modalObject: null,
  });
  const [messageUser, setMessageUser] = useState([]);
  const [authenticate, setAuthenticate] = useState(authData != null);
  const navigate = useNavigate();

  const [newMessageTimeout, setNewMessageTimeout] = useState([]);
  const [proposalNotifications, setProposalNotifications] = useState([]);

  const addProposalNotification = useCallback((notif) => {
    setProposalNotifications((prev) => {
      if (prev.some((n) => n.id === notif.id)) return prev;
      return [notif, ...prev];
    });
  }, []);

  const removeProposalNotification = useCallback((id) => {
    setProposalNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const loginProvider = async (loginData) => {
    try {
      const sessionCode = loginData.tokenInfo;
      const email = loginData.username;

      const authData = await setAuthData("authAdminInfo", loginData);
      if (!authData) {
        setAuthenticate(false);
        return;
      }
      const decodeToken = jwtDecode(authData?.tokenInfo?.access_token);
      const expiresAt = decodeToken?.exp;
      let valid = new Date().getTime() / 1000 < expiresAt;
      if (valid) {
        setAuthenticate(valid);
        setAuthInfo(authData);
        navigate("/dashboard/home", { replace: true });
        return;
      }
      if (sessionCode && email) {
        const authData = await setAuthData("authAdminInfo", sessionCode);
        if (!authData) {
          setAuthenticate(false);
          return;
        }
        const decodeToken = jwtDecode(authData?.tokenInfo?.access_token);
        const expiresAt = decodeToken?.exp;
        let valid = new Date().getTime() / 1000 < expiresAt;
        if (valid) {
          setAuthenticate(valid);
          setAuthInfo(authData);
          navigate("/dashboard/home", { replace: true });
          return;
        }
      }
    } catch (error) {
      setAuthenticate(false);
    }
    setAuthenticate(false);
  };

  const logoutProvider = async (e) => {
    await setAuthData("authAdminInfo", null);
    setAuthenticate(false);
    navigate("/", { replace: true });
  };

  /**
   * resetErrorMsg
   */
  const resetErrorMsg = () => {
    setErrorMsg("");
  };

  /**
   * resetSuccessMsg
   */
  const resetSuccessMsg = () => {
    setSuccessMsg("");
  };

  const formContextValues = useMemo(
    () => ({
      authenticate,
      loading,
      setLoading,
      errorMsg,
      setErrorMsg,
      successMsg,
      messageUser,
      setMessageUser,
      setSuccessMsg,
      modalData,
      setModalData,
      loginProvider,
      logoutProvider,
      authData,
      authInfo,
      resetErrorMsg,
      resetSuccessMsg,
      newMessageTimeout,
      setNewMessageTimeout,
      proposalNotifications,
      addProposalNotification,
      removeProposalNotification,
      isEmbedded,
    }),
    [
      authInfo,
      authenticate,
      loading,
      errorMsg,
      successMsg,
      modalData,
      messageUser,
      newMessageTimeout,
      setNewMessageTimeout,
      proposalNotifications,
      isEmbedded,
    ]
  );

  return (
    <AppContext.Provider value={formContextValues}>
      <WebSocketProvider>
        <SidebarDataContextProvider>
          <CompaniesDataContextProvider>
            <CustomerStatusDataContextProvider>
              <LeadTagDataContextProvider>
                <CountryPhoneContextProvider>
                  <ClientsDataContextProvider>
                    <VendorsDataContextProvider>
                    <ClientsLeadsDataContextProvider>
                      <ClientsLeadsNuevoDataContextProvider>
                        <TemplatesQuickResponseDataContextProvider>
                          <TemplatesTwilioDataContextProvider>
                            <TemplatesEmailDataContextProvider>
                              <AlertProvider>
                                {children}
                              </AlertProvider>
                            </TemplatesEmailDataContextProvider>
                          </TemplatesTwilioDataContextProvider>
                        </TemplatesQuickResponseDataContextProvider>
                      </ClientsLeadsNuevoDataContextProvider>
                    </ClientsLeadsDataContextProvider>
                    </VendorsDataContextProvider>
                  </ClientsDataContextProvider>
                </CountryPhoneContextProvider>
              </LeadTagDataContextProvider>
            </CustomerStatusDataContextProvider>
          </CompaniesDataContextProvider>
        </SidebarDataContextProvider>
      </WebSocketProvider>
    </AppContext.Provider>
  );
};

export { AppProvider };

export default AppContext;

